import api from './api';
import { AnexoCirurgia } from './apontamentos';

/**
 * Dados da cirurgia que o vendedor preencheu no app ao fazer o pedido de
 * URGÊNCIA ou ELETIVA.
 *
 * Eles não moram no `pedido`: o POST /pedidos grava uma linha em `agendamentos`
 * (data, hora, paciente, médico, convênio, observações) ANTES de tentar integrar
 * com o TOTVS — por isso existem mesmo quando o pedido cai em ERRO_INTEGRACAO,
 * e é justamente aí que a logística precisa deles para separar o material na
 * mão até o erro ser resolvido.
 *
 * O GET /pedidos/:id não traz o agendamento, e não há rota por pedido: a única
 * que devolve é GET /agendamentos/:colaborador_id/vendedor (a mesma que o Mapa
 * Cirúrgico usa), então buscamos a lista do vendedor e achamos pelo pedido_id.
 *
 * ATENÇÃO ao caminho: o baseURL desta web é a RAIZ da API — cada chamada
 * escreve o prefixo.
 */

const PREFIXO = '/api-essencial/v1';

/** Tipos de pedido que o backend acompanha de agendamento (criarPedidoUseCase). */
export const TIPOS_COM_CIRURGIA = ['URGENCIA', 'ELETIVA'];

const ROTULO_TIPO: Record<string, string> = {
  URGENCIA: 'Urgência',
  ELETIVA: 'Eletiva',
};

export interface CirurgiaDoPedido {
  agendamentoId: string;
  tipo: string;
  /** `YYYY-MM-DD` — `data_agendamento` é `@db.Date` e chega como meia-noite UTC. */
  data: string;
  hora: string;
  paciente: string;
  hospital: string;
  medico: string;
  medicoCrm: string;
  convenio: string;
  observacoes: string;
}

const normalizar = (valor: any) => String(valor || '').trim().toUpperCase();

export function pedidoTemCirurgia(pedido: any): boolean {
  return TIPOS_COM_CIRURGIA.includes(normalizar(pedido?.tipo_pedido));
}

export function rotuloTipoCirurgia(tipo?: string | null): string {
  return ROTULO_TIPO[normalizar(tipo)] || String(tipo || '');
}

/**
 * O agendamento deste pedido dentro da lista do vendedor. Um pedido desmembrado
 * por fabricante vira vários pedidos, cada um com o SEU agendamento, então o
 * vínculo é sempre pelo pedido_id — nunca por data/paciente.
 */
export function acharAgendamentoDoPedido(agendamentos: any[], pedidoId: string): any | null {
  if (!pedidoId) return null;
  const doPedido = (Array.isArray(agendamentos) ? agendamentos : []).filter(
    (a) => (a?.pedido_id || a?.pedido?.pedido_id) === pedidoId
  );
  return doPedido.find((a) => !a?.excluido) || doPedido[0] || null;
}

export function adaptarCirurgia(agendamento: any): CirurgiaDoPedido {
  return {
    agendamentoId: agendamento?.agendamento_id || '',
    tipo: agendamento?.tipo || '',
    data: String(agendamento?.data_agendamento || '').substring(0, 10),
    hora: agendamento?.hora_agendamento || '',
    paciente: agendamento?.paciente || '',
    hospital: agendamento?.clientes?.fantasia || '',
    medico: agendamento?.medico?.nome || '',
    medicoCrm: agendamento?.medico?.crm || '',
    convenio: agendamento?.convenio?.descricao || '',
    observacoes: agendamento?.observacoes || '',
  };
}

/** null quando o pedido não é de cirurgia ou o agendamento não foi achado. */
export async function buscarCirurgiaDoPedido(pedido: any): Promise<CirurgiaDoPedido | null> {
  if (!pedidoTemCirurgia(pedido) || !pedido?.colaborador_id) return null;

  const { data } = await api.get(
    `${PREFIXO}/agendamentos/${pedido.colaborador_id}/vendedor`
  );
  const agendamento = acharAgendamentoDoPedido(data, pedido.pedido_id);
  return agendamento ? adaptarCirurgia(agendamento) : null;
}

/**
 * Documentos que o vendedor anexou ao agendar (pedido médico, autorização do
 * convênio, exames). Os da etapa APONTAMENTO ficam de fora: são de depois da
 * cirurgia e já aparecem na tela de Apontamentos.
 */
export async function listarAnexosDoAgendamento(agendamentoId: string): Promise<AnexoCirurgia[]> {
  const { data } = await api.get(`${PREFIXO}/agendamentos/${agendamentoId}/anexos`);
  const anexos: AnexoCirurgia[] = Array.isArray(data?.anexos) ? data.anexos : [];
  return anexos.filter((a) => a?.etapa !== 'APONTAMENTO');
}

/** A rota exige token, então o arquivo vem como blob e quem abre cria a object URL. */
export async function baixarAnexoDoAgendamento(
  agendamentoId: string,
  anexoId: string
): Promise<Blob> {
  const { data } = await api.get<Blob>(
    `${PREFIXO}/agendamentos/${agendamentoId}/anexos/${anexoId}`,
    { responseType: 'blob' }
  );
  return data;
}
